import { Lock } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";

/** The shape every saved-filter row needs; the definition type is irrelevant here. */
interface SavedFilterRow {
	id: string;
	name: string;
}

interface Props<T extends SavedFilterRow> {
	savedFilters: T[];
	/** Reorder via ▲/▼ — when omitted the move buttons are hidden. */
	onReorder?: (ordered: T[]) => void;
	/** Delete via × — when omitted the delete button is hidden. */
	onDelete?: (id: string) => void;
	/** Inline rename — when provided the name renders as an editable input. */
	onRename?: (id: string, name: string) => void;
	/** Row click to edit — when provided the name renders as a button that opens the editor. */
	onSelect?: (id: string) => void;
	/** Render as a read-only, dimmed list (e.g. org-managed filters). */
	muted?: boolean;
	/** Override the empty-state text. */
	emptyText?: string;
}

function arrayMove<T>(arr: T[], from: number, to: number): T[] {
	const next = arr.slice();
	const [moved] = next.splice(from, 1);
	next.splice(to, 0, moved);
	return next;
}

/** One saved-filter row — reorder/rename/delete/select affordances are opt-in. */
const FilterRow: React.FC<{
	filter: SavedFilterRow;
	isFirst: boolean;
	isLast: boolean;
	muted?: boolean;
	titles: { up: string; down: string; remove: string };
	onRename?: (id: string, name: string) => void;
	onSelect?: (id: string) => void;
	onDelete?: (id: string) => void;
	onMoveUp?: () => void;
	onMoveDown?: () => void;
}> = ({
	filter,
	isFirst,
	isLast,
	muted,
	titles,
	onRename,
	onSelect,
	onDelete,
	onMoveUp,
	onMoveDown,
}) => {
	// Local draft so renaming doesn't persist on every keystroke; commit on
	// blur / Enter (persistence is a backend round-trip).
	const [name, setName] = useState(filter.name);

	// Keep in sync if the underlying filter changes (e.g. reorder).
	useEffect(() => setName(filter.name), [filter.name]);

	const commit = () => {
		const trimmed = name.trim();
		if (onRename && trimmed && trimmed !== filter.name) onRename(filter.id, trimmed);
		else setName(filter.name);
	};

	return (
		<div
			className={cn(
				"flex items-center gap-2 rounded-sm border border-solid border-gray-200 px-2 py-1.5",
				muted ? "bg-gray-50" : "bg-white"
			)}
		>
			{onMoveUp && onMoveDown && (
				<div className="flex flex-col text-gray-400">
					<button
						type="button"
						title={titles.up}
						disabled={isFirst}
						onClick={onMoveUp}
						className={cn(
							"text-[10px] leading-[10px] hover:text-gray-700",
							isFirst && "cursor-default opacity-30 hover:text-gray-400"
						)}
					>
						▲
					</button>
					<button
						type="button"
						title={titles.down}
						disabled={isLast}
						onClick={onMoveDown}
						className={cn(
							"text-[10px] leading-[10px] hover:text-gray-700",
							isLast && "cursor-default opacity-30 hover:text-gray-400"
						)}
					>
						▼
					</button>
				</div>
			)}

			{muted && <Lock size={12} className="shrink-0 text-gray-400" />}

			{onSelect ? (
				<button
					type="button"
					onClick={() => onSelect(filter.id)}
					className="min-w-0 flex-1 truncate text-left text-sm text-gray-700 hover:text-action"
				>
					{filter.name}
				</button>
			) : onRename ? (
				<input
					className="min-w-0 flex-1 bg-transparent text-sm text-gray-700 outline-none"
					value={name}
					onChange={(e) => setName(e.target.value)}
					onBlur={commit}
					onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
				/>
			) : (
				<span
					className={cn(
						"min-w-0 flex-1 truncate text-sm",
						muted ? "text-gray-400" : "text-gray-700"
					)}
				>
					{filter.name}
				</span>
			)}

			{onDelete && (
				<button
					onClick={() => onDelete(filter.id)}
					title={titles.remove}
					className="px-1 text-gray-400 hover:text-gray-700"
				>
					×
				</button>
			)}
		</div>
	);
};

/**
 * Renders a list of saved filters with opt-in reorder/rename/delete/select
 * affordances, plus a `muted` read-only mode. Generic over the filter type so it
 * serves any domain's saved filters.
 */
function SavedFiltersManager<T extends SavedFilterRow>({
	savedFilters,
	onReorder,
	onDelete,
	onRename,
	onSelect,
	muted,
	emptyText,
}: Props<T>) {
	const { t } = useTranslation();

	const titles = {
		up: t("MOVE_UP" as any),
		down: t("MOVE_DOWN" as any),
		remove: t("DELETE" as any),
	};

	return (
		<div className="flex flex-col gap-4">
			{savedFilters.length ? (
				<div className="flex flex-col gap-1.5">
					{savedFilters.map((filter, index) => (
						<FilterRow
							key={filter.id}
							filter={filter}
							isFirst={index === 0}
							isLast={index === savedFilters.length - 1}
							muted={muted}
							titles={titles}
							onRename={onRename}
							onSelect={onSelect}
							onDelete={onDelete}
							onMoveUp={onReorder && (() => onReorder(arrayMove(savedFilters, index, index - 1)))}
							onMoveDown={onReorder && (() => onReorder(arrayMove(savedFilters, index, index + 1)))}
						/>
					))}
				</div>
			) : (
				<p className="text-sm italic text-gray-400">{emptyText ?? t("NO_SAVED_FILTERS" as any)}</p>
			)}
		</div>
	);
}

export default SavedFiltersManager;
